import React, { useEffect } from 'react';
import { useSpring } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import { logger } from '../../../../utils/logger';

interface UseTaskColumnSwipeProps {
  /**
   * Ordered array of column IDs to navigate between
   */
  columnIds: string[];

  /**
   * Current active column ID
   */
  activeColumnId: string;

  /**
   * Callback to switch to a new column
   */
  onColumnChange: (columnId: string) => void;

  /**
   * Callback to navigate back to topics list (called when swiping right on first column)
   */
  onBackToTopics: () => void;

  /**
   * Whether to enable the swipe gesture
   * @default true
   */
  enabled?: boolean;

  /**
   * Distance threshold as fraction of screen width to trigger navigation
   * @default 0.15
   */
  threshold?: number;
}

export function useTaskColumnSwipe({
  columnIds,
  activeColumnId,
  onColumnChange,
  onBackToTopics,
  enabled = true,
  threshold = 0.15,
}: UseTaskColumnSwipeProps) {
  // Using ref to track current column to avoid dependency issues
  const currentColumnRef = React.useRef(activeColumnId);

  // Update ref whenever the activeColumnId changes
  useEffect(() => {
    currentColumnRef.current = activeColumnId;
  }, [activeColumnId]);

  // Create spring animation for content transform - moderate config
  const [{ x }, api] = useSpring(() => ({
    x: 0,
    config: { tension: 350, friction: 40 }, // Less stiff than before for smoother feel
  }));

  // Reset animation when enabled state changes
  useEffect(() => {
    if (!enabled) {
      api.start({ x: 0 });
    }
  }, [enabled, api]);

  // Memoize these values to avoid recalculation on every render
  const { currentIndex, isFirstColumn, isLastColumn } = React.useMemo(() => {
    const idx = columnIds.indexOf(activeColumnId);
    return {
      currentIndex: idx,
      isFirstColumn: idx === 0,
      isLastColumn: idx === columnIds.length - 1,
    };
  }, [columnIds, activeColumnId]);

  // Set up drag gesture handler
  const bind = useDrag(
    ({ movement: [mx], last, down, velocity: [vx], cancel }) => {
      // Skip if disabled
      if (!enabled) {
        cancel?.();
        return;
      }

      // Calculate screen width for thresholds
      const screenWidth = window.innerWidth;
      const thresholdDistance = screenWidth * threshold;

      // Determine swipe direction
      const isRightSwipe = mx > 0;
      const isLeftSwipe = mx < 0;

      // Exit early if swiping in an invalid direction
      // - Can't swipe left (forward) on last column
      // - Can navigate anywhere with right swipe
      if (isLeftSwipe && isLastColumn) {
        cancel?.();
        return;
      }

      // Apply spring animation during drag - more visible now
      if (down) {
        // Resistance factor - lower means less resistance to dragging
        const resistance = 3; // Reduced resistance for more visible movement

        // Limit the maximum distance for moderate movement
        const maxDistance = screenWidth * 0.25; // Increased to 25% of screen width

        // Apply resistance to make it harder to drag past limits
        let movement = mx;

        // Apply moderate resistance for more visible effect
        movement = movement / resistance;

        // Apply maximum distance constraint
        if (movement > maxDistance) movement = maxDistance;
        if (movement < -maxDistance) movement = -maxDistance;

        // Additional resistance at boundaries (but less than before)
        if ((isFirstColumn && isRightSwipe) || (isLastColumn && isLeftSwipe)) {
          movement = movement / 1.5; // Less resistance at boundaries
        }

        // Update position with spring - using immediate: false for smoother feel
        api.start({ x: movement, immediate: false });
      }

      // When the drag is released
      if (last) {
        const isOverThreshold = Math.abs(mx) > thresholdDistance;
        const isHighVelocity = Math.abs(vx) > 0.3;
        const shouldNavigate = isOverThreshold || isHighVelocity;

        if (shouldNavigate) {
          if (isRightSwipe) {
            // Right swipe - go back
            if (isFirstColumn) {
              // If on first column, navigate back to topics list
              logger.component('Column swipe: First column, navigating back to topics');

              // For back navigation to topics, ensure everything is reset first
              // Force a complete animation reset before navigating
              api.start({
                x: 0,
                immediate: true,
                onRest: () => {
                  // Only after we're sure position is reset, navigate back
                  onBackToTopics();
                },
              });
            } else {
              // Navigate to previous column
              const prevColumnId = columnIds[currentIndex - 1];
              logger.component('Column swipe: Moving to previous column', {
                from: activeColumnId,
                to: prevColumnId,
              });

              // Ensure proper reset before navigation
              api.start({
                x: 0,
                immediate: true,
                onRest: () => {
                  // Navigate only after position is fully reset
                  onColumnChange(prevColumnId);
                },
              });
            }
          } else if (isLeftSwipe && !isLastColumn) {
            // Left swipe - go forward to next column
            const nextColumnId = columnIds[currentIndex + 1];
            logger.component('Column swipe: Moving to next column', {
              from: activeColumnId,
              to: nextColumnId,
            });

            // Ensure proper reset before navigation
            api.start({
              x: 0,
              immediate: true,
              onRest: () => {
                // Navigate only after position is fully reset
                onColumnChange(nextColumnId);
              },
            });
          }
        } else {
          // Not enough distance/velocity to navigate, reset with a spring animation
          // Using a moderate spring for natural reset
          api.start({
            x: 0,
            config: { tension: 400, friction: 40 },
            immediate: false,
          });
        }
      }
    },
    {
      // Apply drag only to horizontal movements
      axis: 'x',
      // Use touch events for mobile with passive: true to avoid the warning
      pointer: { touch: true, capture: false, passive: true },
      // Filter out small movements
      filterTaps: true,
      // No delay to make it feel responsive
      delay: 0,
    }
  );

  return {
    bind,
    x,
  };
}
